import React, { useEffect, useState } from 'react';
import { useOnboardingStore, type SelectedService } from '../store';
import { BusinessInfo } from '../components/steps/BusinessInfo';
import { AccountCreation } from '../components/steps/AccountCreation';
import { ServiceTypeSelection } from '../components/steps/ServiceTypeSelection';
import { ServiceSelection } from '../components/steps/ServiceSelection';
import { Costs } from '../components/steps/Costs';
import { IntegrationSetup } from '../components/steps/IntegrationSetup';
import { Success } from '../components/steps/Success';
import { Building2, CircleDollarSign, Link, CircleUser as UserCircle2, CheckCircle2, UserCheck, Info, ShieldCheck } from 'lucide-react';
import { ProgressBar } from '../components/ProgressBar';
import { CostCalculator } from '../components/CostCalculator';
import { Header } from '../components/Header';
import { isDeveloperMode } from '../config/devMode';
import ServiceImplementationModal from '../components/ServiceImplementationModal';
import WelcomePopup from '../components/WelcomePopup';
import { restoreOnboardingLead, unsubscribeOnboardingReminders } from '../lib/leadCapture';
import toast from 'react-hot-toast';
import type { ServiceTier, ServiceType } from '../types';

const steps = [
  { title: 'Business Information', icon: UserCircle2, component: BusinessInfo },
  { title: 'Review Contact', icon: UserCheck, component: AccountCreation },
  { title: 'Choose Service', icon: Building2, component: ServiceTypeSelection },
  { title: 'Select Plan', icon: CircleDollarSign, component: ServiceSelection },
  { title: 'Costs', icon: CircleDollarSign, component: Costs },
  { title: 'Integrations', icon: Link, component: IntegrationSetup },
  { title: 'Complete', icon: CheckCircle2, component: Success }
];

const setupPath = [
  {
    title: 'Tell us only what matters',
    detail: 'Business and contact details save as you type.'
  },
  {
    title: 'Pick services',
    detail: 'Choose what you want Scale Small AI to run.'
  },
  {
    title: 'Review costs',
    detail: 'See pricing before any Stripe payment step.'
  },
  {
    title: 'Connect platforms',
    detail: 'Choose socials, Search Console, photo sources, CRM, and billing.'
  },
  {
    title: 'Dashboard review',
    detail: 'The Home tracker checks setup and sends it to review.'
  },
  {
    title: 'Services start',
    detail: 'After setup, admin approval, and dashboard billing/payment setup, services can begin.'
  }
];

function draftString(source: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return '';
}

function draftArray<T>(source: Record<string, unknown>, key: string): T[] | null {
  const value = source[key];
  return Array.isArray(value) ? value as T[] : null;
}

export function OnboardingFlow() {
  const {
    currentStep,
    setStep,
    selectedTier,
    selectedServiceTypes,
    selectedServices,
    client,
    updateClient,
    setSelectedServiceTypes,
    setSelectedTier,
    setSelectedServices
  } = useOnboardingStore();
  const CurrentStepComponent = steps[currentStep].component;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const restoreAttemptedRef = React.useRef(false);

  // Scroll to top whenever the current step changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, [currentStep]);

  useEffect(() => {
    if (restoreAttemptedRef.current) return;
    restoreAttemptedRef.current = true;
    let cancelled = false;

    async function restoreDraft() {
      try {
        const params = new URLSearchParams(window.location.search);
        const unsubscribeToken = params.get('unsubscribe');
        if (unsubscribeToken) {
          await unsubscribeOnboardingReminders(unsubscribeToken);
          toast.success('Onboarding reminders unsubscribed.');
          params.delete('unsubscribe');
          const nextSearch = params.toString();
          window.history.replaceState({}, '', `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`);
        }

        const restored = await restoreOnboardingLead();
        if (cancelled || !restored.lead) return;

        const draft = restored.lead.draft_payload || {};
        const advisorDraft = (
          draft.prospect_advisor
          && typeof draft.prospect_advisor === 'object'
          && !Array.isArray(draft.prospect_advisor)
        )
          ? draft.prospect_advisor as Record<string, unknown>
          : {};

        updateClient({
          businessName: draftString(draft, 'businessName', 'business_name')
            || draftString(advisorDraft, 'business_name', 'businessName')
            || restored.lead.business_name
            || client.businessName
            || '',
          contactName: draftString(draft, 'contactName', 'contact_name')
            || draftString(advisorDraft, 'contact_name', 'contactName')
            || restored.lead.contact_name
            || client.contactName
            || '',
          email: draftString(draft, 'email')
            || draftString(advisorDraft, 'email')
            || restored.lead.email
            || client.email
            || '',
          phone: draftString(draft, 'phone')
            || draftString(advisorDraft, 'phone')
            || restored.lead.phone
            || client.phone
            || '',
          address: draftString(draft, 'address', 'cityState', 'city_state')
            || draftString(advisorDraft, 'address', 'city_state', 'cityState')
            || restored.lead.address
            || client.address
            || '',
        });

        const draftServiceTypes = draftArray<ServiceType>(draft, 'selectedServiceTypes');
        if (draftServiceTypes && selectedServiceTypes.length === 0) {
          setSelectedServiceTypes(draftServiceTypes);
        }
        if (draft.selectedTier && !selectedTier) {
          setSelectedTier(draft.selectedTier as ServiceTier);
        }
        const draftServices = draftArray<SelectedService>(draft, 'selectedServices');
        if (draftServices && selectedServices.length === 0) {
          setSelectedServices(draftServices);
        }

        if (typeof restored.lead.current_step === 'number' && restored.lead.current_step > currentStep) {
          setStep(Math.min(restored.lead.current_step, steps.length - 1));
        }

      } catch (error) {
        console.log('Draft restore skipped:', error);
      }
    }

    restoreDraft();
    return () => {
      cancelled = true;
    };
  }, [
    client.address,
    client.businessName,
    client.contactName,
    client.email,
    client.phone,
    currentStep,
    selectedServiceTypes.length,
    selectedServices.length,
    selectedTier,
    setSelectedServiceTypes,
    setSelectedServices,
    setSelectedTier,
    setStep,
    updateClient
  ]);

  function isStepAccessible(index: number) {
    if (isDeveloperMode) return true;
    if (index <= currentStep) return true;

    if (index === 1 && client.businessName) return true;
    if (index === 2 && client.email) return true;
    if (index === 3 && selectedServiceTypes.length > 0) return true;
    if (index === 4 && selectedTier) return true;
    if (index === 5 && (selectedServices.length > 0 || selectedTier)) return true;
    if (index === 6 && client.integrations_completed) return true;

    return false;
  }

  function handleStepClick(index: number) {
    if (!isStepAccessible(index)) return;
    setStep(index);
  }

  return (
    <div className="min-h-screen bg-scale-dark">
      <Header />

      <div className="max-w-6xl mx-auto px-4 pt-20 md:pt-28 pb-8">
        <header className="text-center mb-8 md:mb-12">
          <h1 className="text-3xl md:text-5xl font-bold gradient-text mb-2 md:mb-4">
            Scale Your Business
          </h1>
          <p className="text-lg md:text-xl text-scale-blue mb-4">
            Content Automation, Proof-of-Work Publishing, Customer Engagement, and Local Presence
          </p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
          >
            <Info className="w-4 h-4" />
            View Implementation Process
          </button>
        </header>

        <section className="mb-8 rounded-xl border border-green-400/25 bg-scale-navy/50 p-4 text-left shadow-xl shadow-black/10 md:p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase text-green-300">Saved as you go</p>
              <h2 className="mt-1 text-lg font-semibold text-white md:text-xl">
                A short setup path, not homework.
              </h2>
              <p className="mt-1 max-w-3xl text-sm leading-6 text-gray-300">
                Your answers save in the background. If you leave and come back, we pick up where you stopped.
              </p>
            </div>
            <div className="inline-flex w-fit items-center gap-2 rounded-lg border border-green-400/25 bg-green-400/10 px-3 py-2 text-sm font-medium text-green-200">
              <ShieldCheck className="h-4 w-4 flex-shrink-0" />
              Final review before services start
            </div>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-5">
            {setupPath.map((item, index) => (
              <div key={item.title} className="rounded-lg border border-white/10 bg-scale-dark/40 p-3">
                <p className="text-xs font-semibold text-scale-blue">Step {index + 1}</p>
                <h3 className="mt-1 text-sm font-semibold leading-5 text-white">{item.title}</h3>
                <p className="mt-1 text-xs leading-5 text-gray-400">{item.detail}</p>
              </div>
            ))}
          </div>
        </section>

        <ProgressBar currentStep={currentStep} totalSteps={steps.length} />

        <div className="mb-8 md:mb-12 overflow-x-auto pb-4 md:pb-0">
          <div className="flex justify-start md:justify-center items-center space-x-4 min-w-max md:min-w-0">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index <= currentStep;
              const accessible = isStepAccessible(index);
              
              return (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <div
                      className={`h-1 w-8 md:w-16 ${
                        isActive ? 'bg-scale-blue' : 'bg-gray-700'
                      }`}
                    />
                  )}
                  <div
                    onClick={() => handleStepClick(index)}
                    className={`group flex flex-col items-center ${
                      accessible ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                    }`}
                  >
                    <div
                      className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                        index === currentStep
                          ? 'bg-scale-blue text-scale-dark'
                          : isActive
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-700 text-gray-400 group-hover:bg-scale-blue group-hover:text-scale-dark'
                      }`}
                    >
                      <Icon className="w-5 h-5 md:w-6 md:h-6" />
                    </div>
                    <span
                      className={`text-xs md:text-sm mt-2 transition-colors text-center ${
                        index === currentStep 
                          ? 'text-scale-blue' 
                          : isActive 
                          ? 'text-green-400' 
                          : 'text-gray-400 group-hover:text-scale-blue'
                      }`}
                    >
                      {step.title}
                    </span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Main content area */}
        <div className="w-full">
          <div className="w-full flex justify-center mb-8">
            <div className={`bg-scale-navy bg-opacity-50 backdrop-blur-lg rounded-xl shadow-2xl p-4 md:p-8 border border-gray-800 w-full ${
              currentStep === 3 ? 'max-w-7xl' : 'max-w-4xl'
            }`}>
              <CurrentStepComponent />
            </div>
          </div>
          
          {/* Cost calculator */}
          <div className="w-full flex justify-center">
            <div className="w-full max-w-md">
              <CostCalculator />
            </div>
          </div>
        </div>
      </div>

      <WelcomePopup onViewProcess={() => setIsModalOpen(true)} />

      <ServiceImplementationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
}
